import React, { useEffect, useState } from 'react'
import { CardNew } from './CardNew'
import { Get_Doc_List, ShowDoc, Upload_Doc } from '../Property_Api'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useDropzone } from 'react-dropzone'
import Swal from 'sweetalert2'
import { CircularProgress } from '@mui/material'

interface propsDataType {
  id: string | undefined
}
interface documentTypeDataType {
  created_date: string
  property_document_type_id: number
  property_document_type_key: string
  property_document_type_name: string
  property_document_type_status: number
  property_document_type_uuid: string
}
interface documentsDataType {
  document_label: string
  document_path: string
  property_document_type_name: string
  property_document_uuid: string
}
const PropertyDocumentsUpload = ({ id }: propsDataType) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [addDocuments, setAddDocuments] = useState<boolean>(false)
  const [documentType, setDocumentType] = useState<documentTypeDataType[]>([])
  const [documents, setDocuments] = useState<documentsDataType[]>([])
  const [uploadDocumentLoading, setUploadDocumentLoading] = useState<boolean>(false)

  const getPropertyType = async () => {
    try {
      const { data: res } = await Get_Doc_List()
      if (res.code === '200') {
        setDocumentType(res.data)
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
          text: res.msg_details,
          allowOutsideClick: true,
        })
      }
    }
    catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Error',
        text: error,
        allowOutsideClick: true,
      })
    }
  }

  const getDoc = async () => {
    if (id) {
      try {
        const { data: res } = await ShowDoc(id)
        if (res.code === '200' || res.code === '500') {
          setDocuments(res.data)
        }
        else {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
            text: res.msg_details,
            allowOutsideClick: true,
          })
        }
      }
      catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'Error',
          text: error,
          allowOutsideClick: true,
        })
      }
    }
  }

  useEffect(() => {
    setLoading(true)
    const fetchData = async () => {
      try {
        await Promise.all([getPropertyType(), getDoc()])
      } catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
        })
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const initialValues = {
    property_document_type_uuid: '',
    document_label: '',
  }
  const validationSchema = Yup.object({
    property_document_type_uuid: Yup.string().required('Document type is required'),
    document_label: Yup.string().required('Document name is required'),
  })
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => handleDocumentUpload(),
  })

  // dropzone code start
  const { acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
    },
  })

  const acceptedFileItems = acceptedFiles.map((file) => {
    return (
      // @ts-ignore
      <li key={file.path}>
        {
          // @ts-ignore
          file.path
        }{' '}
        - {file.size} bytes
      </li>
    )
  })

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    // @ts-ignore
    <li key={file.path}>
      {
        // @ts-ignore
        file.path
      }{' '}
      - {file.size} bytes
      <ul>
        {errors.map((e) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ))
  // dropzone code ends

  const handleDocumentUpload = () => {
    if (acceptedFiles.length === 1 && id) {
      setUploadDocumentLoading(true)
      try {
        const reader = new FileReader()
        reader.onloadend = async () => {
          const base64String = reader.result
          const res = await Upload_Doc(
            formik.values.property_document_type_uuid,
            formik.values.document_label,
            id,
            // @ts-ignore
            base64String.split(',')[1]
          )
          if (res.status === 200) {
            getDoc()
            formik.resetForm()
            acceptedFiles.pop()
            Swal.fire({
              position: 'center',
              icon: 'success',
              title: res.data.msg.charAt(0).toUpperCase() + res.data.msg.slice(1),
              text: res.data.msg_details,
              allowOutsideClick: true,
            })
            setUploadDocumentLoading(false)
            setAddDocuments(false)
          } else {
            Swal.fire({
              position: 'center',
              icon: 'error',
              title: res.data.msg.charAt(0).toUpperCase() + res.data.msg.slice(1),
              text: res.data.msg_details,
              allowOutsideClick: true,
            })
            setUploadDocumentLoading(false)
          }
        }
        reader.readAsDataURL(acceptedFiles[0])
      }
      catch (error: any) {
        setUploadDocumentLoading(false)
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'Error',
          text: error,
          allowOutsideClick: true,
        })
      }
    } else {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'No document found',
        text: 'Add document to upload',
        allowOutsideClick: true,
      })
    }
  }

  return (
    <>
      {loading ? (
        <div
          className='card mb-6 mb-xl-10 d-flex justify-content-center align-items-center vh-70'
          style={{ height: '70vh' }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div className='card mb-6 mb-xl-10'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <div className='card-title m-0'>
              <h3 className='fw-bold m-0'>Documents</h3>
            </div>
            <div className='d-flex flex-column pt-3'>
              {addDocuments ? (
                <div className='align-self-center'>
                  <button
                    className='btn btn-sm btn-light fw-bold bg-color-white shadow me-4 btn-active-light-primary'
                    onClick={() => setAddDocuments(false)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                documents.length < 15 && (
                  <button
                    className='align-self-center btn btn-sm btn-primary me-3'
                    onClick={() => setAddDocuments(true)}
                  >
                    Add Documents
                  </button>
                )
              )}
              <p className='text-muted fw-bold mb-0'>{`${documents.length}/15 documents uploaded`}</p>
            </div>
          </div>

          {addDocuments ? (
            <div id='kt_account_profile_details' className='collapse show'>
              <form onSubmit={formik.handleSubmit} className='form'>
                <div className='card-body border-top p-9'>
                  <div className='row mb-3 px-3'>
                    {/*begin::Form Group document type */}
                    <div className='fv-row col-lg-6 col-12 flex-basis-50 align-items-center'>
                      <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                        <span className='required'>Document Type</span>
                        <i
                          className='fas fa-exclamation-circle ms-2 fs-7'
                          data-bs-toggle='tooltip'
                          title='Choose your document type'
                        ></i>
                      </label>
                      <select
                        className='form-select form-select-solid'
                        {...formik.getFieldProps('property_document_type_uuid')}
                        name='property_document_type_uuid'
                        placeholder='Select document type'
                      >
                        <option className='bg-color-gray' value='' disabled>
                          Select document type
                        </option>
                        {documentType?.map((item) => {
                          return (
                            <option
                              key={item.property_document_type_id}
                              value={item.property_document_type_uuid}
                            >
                              {item.property_document_type_name}
                            </option>
                          )
                        })}
                      </select>
                      <div className='h-20px'>
                        {formik.touched.property_document_type_uuid &&
                          formik.errors.property_document_type_uuid && (
                            <div className='fv-help-block color-red'>
                              {formik.errors.property_document_type_uuid}
                            </div>
                          )}
                      </div>
                    </div>
                    {/*end::Form Group */}
                    {/*begin::Form Group document title */}
                    <div className='fv-row col-lg-6 col-12 flex-basis-50 align-items-center'>
                      <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                        <span className='required'>Document Title</span>
                        <i
                          className='fas fa-exclamation-circle ms-2 fs-7'
                          data-bs-toggle='tooltip'
                          title='Specify your document title'
                        ></i>
                      </label>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid'
                        {...formik.getFieldProps('document_label')}
                        name='document_label'
                        placeholder='Enter document title'
                      />
                      <div className='h-20px'>
                        {formik.touched.document_label && formik.errors.document_label && (
                          <div className='fv-help-block color-red'>{formik.errors.document_label}</div>
                        )}
                      </div>
                    </div>
                    {/*end::Form Group */}
                  </div>
                  {/*begin::Dropzone field */}
                  <div className='border border-2 rounded border-dashed row p-2 bg-white mx-4'>
                    <section className='border border-2 rounded border-primary border-dashed h-100 symbol bg-light-primary'>
                      <div
                        {...getRootProps({
                          className:
                            'py-4 d-flex flex-column justify-content-center align-items-center',
                        })}
                      >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                          <p>Drop the files here...</p>
                        ) : (
                          <p>Drag and drop files here, or click to select files</p>
                        )}
                        <i className='bi bi-file-earmark-arrow-up-fill fs-4x btn btn-active-color-primary py-0 mb-3'></i>
                        <span className='text-primary fs-7'>
                          Type: *.jpeg, *.jpg, *.png, *.pdf, *.doc or *.docx only
                        </span>
                        <span className='text-primary fs-7'>Max document:1</span>
                      </div>
                    </section>
                  </div>

                  <aside className='px-5'>
                    {acceptedFileItems.length > 0 && (
                      <div className='pt-2'>
                        <h4>Document is ready to upload</h4>
                        <ul>{acceptedFileItems}</ul>
                      </div>
                    )}
                    {fileRejectionItems.length > 0 && (
                      <div className='pt-2'>
                        <span className='fv-help-block color-red'>
                          Upload only one file or select files with allowed formats only
                        </span>
                        {/* <ul>{fileRejectionItems}</ul> */}
                      </div>
                    )}
                  </aside>
                  {/*end::Dropzone field */}
                  <div className='d-flex flex-column align-items-center'>
                    <button
                      type='submit'
                      className='btn btn-sm btn-primary shadow mt-3'
                      style={{ cursor: 'pointer' }}
                      disabled={uploadDocumentLoading}
                    >
                      {uploadDocumentLoading ? (
                        <span className='indicator-progress' style={{ display: 'block' }}>
                          Processing...
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      ) : (
                        <span>Upload document</span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          ) : documents.length === 0 ? (
            <div className='d-flex flex-column align-items-center flex-start p-8'>
              <img
                src='/media/svg/undraw/document.svg'
                alt='No document uploaded'
                className='h-200px w-200px'
              />
              <span className='fw-bold text-muted fs-1 text-center'>No Document Uploaded!</span>
            </div>
          ) : (
            // <div className='d-flex justify-content-around align-items-center flex-wrap g-3 g-xl-5 mb-1 mb-xl-3 mt-8'>
            <div className='row g-3 p-5 '>
              {documents.map((item) => {
                return (
                  <div
                    key={item.property_document_uuid}
                    className='col-4 col-lg-2'
                  // className='mb-4 mr-2 shadow-sm p-3 mb-5 bg-white rounded'
                  // style={{minWidth: '18%'}}
                  >
                    <CardNew
                      icon='/media/svg/files/file.svg'
                      property_document_uuid={item.property_document_uuid}
                      getDoc={getDoc}
                      title={item.document_label}
                      description={item.property_document_type_name}
                      link={item.document_path}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}</>
  )
}

export default PropertyDocumentsUpload
